import moment from "moment";
import PropTypes from "prop-types";
import Dialog from "../Dialog";
import MUITextField from "@mui/material/TextField";
import MUIFormLabel from "@mui/material/FormLabel";
import MUIFormControl from "@mui/material/FormControl";
import MUIFormControlLabel from "@mui/material/FormControlLabel";
import MUIRadio from "@mui/material/Radio";
import MUIRadioGroup from "@mui/material/RadioGroup";
import MUIDialogActions from "@mui/material/DialogActions";
import MUITypography from "@mui/material/Typography";
import MUIButton from "@mui/material/Button";
import MUIBox from "@mui/material/Box";
import MUIList from "@mui/material/List";
import MUIListItem from "@mui/material/ListItem";
import MUIListItemText from "@mui/material/ListItemText";
import MUIGrid from "@mui/material/Grid2";
import MUIPersonIcon from "@mui/icons-material/Person";
import MUIArrowBackIcon from "@mui/icons-material/ArrowBack";
import MUIArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MUIStack from "@mui/material/Stack";
import InsuranceVerification from "../InsuranceVerification";
import { DatePicker as MUIDatePicker } from "@mui/x-date-pickers/DatePicker";
import Progress from "../Progress";
import MainContext from "../../Context/MainContext/MainContext";

export default class DialogIntakeQuestionnaire extends Dialog {
  static contextType = MainContext; // For legacy class components

  constructor(props) {
    super(props);

    this.state.intakeQuestionnaire = this.props.intakeQuestionnaire;
    this.state.order = this.props.order;
    this.state.step = 0;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.open !== this.props.open) {
      this.setState({
        intakeQuestionnaire: {
          meta: {
            version: moment("2026-02-16") || null,
          },
        },
        step: 0,
      });
    }

    if (
      this.props.readOnly &&
      JSON.stringify(prevProps.intakeQuestionnaire) !==
        JSON.stringify(this.props.intakeQuestionnaire)
    ) {
      this.setState({
        intakeQuestionnaire: this.props.intakeQuestionnaire,
      });
    }

    if (
      this.props.order &&
      JSON.stringify(prevProps.order) !== JSON.stringify(this.props.order)
    ) {
      this.setState({
        order: this.props.order,
      });
    }
  }

  /**
   * Render header title of dialog
   */
  renderHeader() {
    if (this.props.isLoading) {
      return null;
    }
    return (
      <>
        <MUITypography variant="h6" color="textPrimary">
          {this.state.step === 0
            ? "Intake - Insurance"
            : "Intake - Questionnaire"}
        </MUITypography>
        <MUIBox sx={{ marginTop: 2 }}>{this.renderPatientInformation()}</MUIBox>
      </>
    );
  }

  /**
   * Render patient contact and insurance information on top of the dialog.
   */
  renderPatientInformation() {
    const { row } = this.props;

    if (!row) return null;

    return (
      <MUIGrid container spacing={1}>
        {this.renderPatientInfoSection(
          `${this.state.order?.patient?.firstName} ${this.state.order?.patient?.lastName}`,
          this.state.order?.patient?.phone,
        )}
      </MUIGrid>
    );
  }

  /**
   * Render patient name and phone number with person icon
   */
  renderPatientInfoSection(name, phone) {
    return (
      <MUIGrid
        size={{
          lg: 4,
          md: 4,
          sm: 4,
          xs: 12,
        }}
      >
        <MUIBox sx={{ display: "flex", alignItems: "center" }}>
          <MUIPersonIcon sx={{ fontSize: 40 }} />
          <MUIBox sx={{ display: "flex", flexDirection: "column" }}>
            <MUITypography variant="subtitle2">{name}</MUITypography>
            <MUITypography variant="caption">
              {phone
                ? phone
                    .replace(/\D/g, "")
                    .replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")
                    .padStart(14, phone)
                : null}
            </MUITypography>
          </MUIBox>
        </MUIBox>
      </MUIGrid>
    );
  }

  /**
   * Render main content of dialog
   */
  renderContent() {
    return <MUIBox>{this.renderBody()}</MUIBox>;
  }

  handleChange = (updatedIntakeQuestionnaire) => {
    this.setState({
      intakeQuestionnaire: {
        ...this.state.intakeQuestionnaire,
        ...updatedIntakeQuestionnaire,
      },
    });
  };

  renderHomeHealth() {
    return (
      <MUIBox>
        <MUITypography variant="body1" sx={{ marginBottom: 2 }}>
          Does the patient have Home Health?
        </MUITypography>
        <MUIGrid
          container
          spacing={1}
          sx={{
            alignItems: "center",
          }}
        >
          <MUIGrid
            size={{
              lg: 3,
              xs: 12,
            }}
          >
            <MUIFormControl
              component="fieldset"
              required
              error={this.props.validationIssues.homeHealth ? true : false}
            >
              <MUIFormLabel sx={{ typography: "caption" }}>
                Select One
              </MUIFormLabel>
              <MUIRadioGroup
                row
                value={
                  this.state.intakeQuestionnaire.homeHealth !== undefined
                    ? this.state.intakeQuestionnaire.homeHealth
                    : null
                }
                onChange={(e) =>
                  this.handleChange({ homeHealth: e.target.value === "true" })
                }
              >
                <MUIFormControlLabel
                  disabled={this.props.readOnly}
                  value={true}
                  control={<MUIRadio size="small" />}
                  label="Yes"
                />
                <MUIFormControlLabel
                  disabled={this.props.readOnly}
                  value={false}
                  control={<MUIRadio size="small" />}
                  label="No"
                />
              </MUIRadioGroup>
            </MUIFormControl>
          </MUIGrid>
          <MUIGrid
            size={{
              lg: 9,
              xs: 12,
            }}
          >
            <MUITextField
              disabled={this.props.readOnly}
              value={
                this.state.intakeQuestionnaire
                  ? this.state.intakeQuestionnaire.homeHealthComments
                  : ""
              }
              multiline
              fullWidth={true}
              label={"Comments (optional)"}
              onChange={(e) =>
                this.handleChange({ homeHealthComments: e.target.value })
              }
              variant="standard"
              slotProps={{
                htmlInput: { style: { resize: "both" } },
                inputLabel: { shrink: true },
              }}
            />
          </MUIGrid>
        </MUIGrid>
      </MUIBox>
    );
  }

  renderConfirmedInsurance() {
    return (
      <MUIBox>
        <MUITypography variant="body1" sx={{ marginBottom: 2 }}>
          Has the patient's insurance been confirmed?
        </MUITypography>
        <MUIGrid
          container
          spacing={1}
          sx={{
            alignItems: "center",
          }}
        >
          <MUIGrid
            size={{
              lg: 3,
              xs: 12,
            }}
          >
            <MUIFormControl
              component="fieldset"
              required
              error={
                this.props.validationIssues.confirmedInsurance ? true : false
              }
            >
              <MUIFormLabel sx={{ typography: "caption" }}>
                Select One
              </MUIFormLabel>
              <MUIRadioGroup
                row
                value={
                  this.state.intakeQuestionnaire.confirmedInsurance !==
                  undefined
                    ? this.state.intakeQuestionnaire.confirmedInsurance
                    : null
                }
                onChange={(e) =>
                  this.handleChange({
                    confirmedInsurance: e.target.value === "true",
                  })
                }
              >
                <MUIFormControlLabel
                  disabled={this.props.readOnly}
                  value={true}
                  control={<MUIRadio size="small" />}
                  label="Yes"
                />
                <MUIFormControlLabel
                  disabled={this.props.readOnly}
                  value={false}
                  control={<MUIRadio size="small" />}
                  label="No"
                />
              </MUIRadioGroup>
            </MUIFormControl>
          </MUIGrid>
          <MUIGrid
            size={{
              lg: 9,
              xs: 12,
            }}
          >
            <MUITextField
              disabled={this.props.readOnly}
              value={
                this.state.intakeQuestionnaire
                  ? this.state.intakeQuestionnaire.confirmedInsuranceComments
                  : ""
              }
              multiline
              fullWidth={true}
              label={"Comments (optional)"}
              onChange={(e) =>
                this.handleChange({
                  confirmedInsuranceComments: e.target.value,
                })
              }
              variant="standard"
              slotProps={{
                htmlInput: { style: { resize: "both" } },
                inputLabel: { shrink: true },
              }}
            />
          </MUIGrid>
        </MUIGrid>
      </MUIBox>
    );
  }

  renderPlanToChangeInsurance() {
    return (
      <MUIBox>
        <MUITypography variant="body1" sx={{ marginBottom: 2 }}>
          Does the patient plan on keeping the same insurance during this
          process?
        </MUITypography>
        <MUIGrid
          container
          spacing={1}
          sx={{
            alignItems: "center",
          }}
        >
          <MUIGrid
            size={{
              lg: 3,
              xs: 12,
            }}
          >
            <MUIFormControl
              component="fieldset"
              required
              error={
                this.props.validationIssues.planToChangeInsurance ? true : false
              }
            >
              <MUIFormLabel sx={{ typography: "caption" }}>
                Select One
              </MUIFormLabel>
              <MUIRadioGroup
                row
                value={
                  this.state.intakeQuestionnaire.planToChangeInsurance !==
                  undefined
                    ? this.state.intakeQuestionnaire.planToChangeInsurance
                    : null
                }
                onChange={(e) =>
                  this.handleChange({
                    planToChangeInsurance: e.target.value === "true",
                  })
                }
              >
                <MUIFormControlLabel
                  disabled={this.props.readOnly}
                  value={true}
                  control={<MUIRadio size="small" />}
                  label="Yes"
                />
                <MUIFormControlLabel
                  disabled={this.props.readOnly}
                  value={false}
                  control={<MUIRadio size="small" />}
                  label="No"
                />
              </MUIRadioGroup>
            </MUIFormControl>
          </MUIGrid>
          <MUIGrid
            size={{
              lg: 9,
              xs: 12,
            }}
          >
            <MUITextField
              disabled={this.props.readOnly}
              value={
                this.state.intakeQuestionnaire
                  ? this.state.intakeQuestionnaire.planToChangeInsuranceComments
                  : ""
              }
              multiline
              fullWidth={true}
              label={"Comments (optional)"}
              onChange={(e) =>
                this.handleChange({
                  planToChangeInsuranceComments: e.target.value,
                })
              }
              variant="standard"
              slotProps={{
                htmlInput: { style: { resize: "both" } },
                inputLabel: { shrink: true },
              }}
            />
          </MUIGrid>
        </MUIGrid>
      </MUIBox>
    );
  }

  renderPhysicianDate() {
    return (
      <MUIBox>
        <MUITypography variant="body1" sx={{ marginBottom: 2 }}>
          When is the date of the patient's last physician visit?
        </MUITypography>
        <MUIDatePicker
          disabled={this.props.readOnly}
          value={
            this.state.intakeQuestionnaire &&
            this.state.intakeQuestionnaire.lastPhysicianVisitDate
              ? moment(this.state.intakeQuestionnaire.lastPhysicianVisitDate)
              : null
          }
          label="Select Date"
          slotProps={{
            textField: {
              size: "small",
              variant: "standard",
              required: true,
              error: this.props.validationIssues.lastPhysicianVisitDate
                ? true
                : false,
              helperText: this.props.validationIssues.lastPhysicianVisitDate
                ? this.props.validationIssues.lastPhysicianVisitDate
                : "",
              InputLabelProps: {
                shrink: true,
              },
            },
          }}
          onChange={(value) =>
            this.handleChange({ lastPhysicianVisitDate: value })
          }
          views={["year", "month", "day"]}
        />
      </MUIBox>
    );
  }

  renderPatientCommit() {
    return (
      <MUIBox>
        <MUITypography variant="body1" sx={{ marginBottom: 2 }}>
          Does the patient commit to the process and to attending evaluations?
        </MUITypography>
        <MUIGrid
          container
          spacing={1}
          sx={{
            alignItems: "center",
          }}
        >
          <MUIGrid
            size={{
              lg: 3,
              xs: 12,
            }}
          >
            <MUIFormControl
              component="fieldset"
              required
              error={
                this.props.validationIssues.patientCommitToProcess
                  ? true
                  : false
              }
            >
              <MUIFormLabel sx={{ typography: "caption" }}>
                Select One
              </MUIFormLabel>
              <MUIRadioGroup
                row
                value={
                  this.state.intakeQuestionnaire.patientCommitToProcess !==
                  undefined
                    ? this.state.intakeQuestionnaire.patientCommitToProcess
                    : null
                }
                onChange={(e) =>
                  this.handleChange({
                    patientCommitToProcess: e.target.value === "true",
                  })
                }
              >
                <MUIFormControlLabel
                  disabled={this.props.readOnly}
                  value={true}
                  control={<MUIRadio size="small" />}
                  label="Yes"
                />
                <MUIFormControlLabel
                  disabled={this.props.readOnly}
                  value={false}
                  control={<MUIRadio size="small" />}
                  label="No"
                />
              </MUIRadioGroup>
            </MUIFormControl>
          </MUIGrid>
          <MUIGrid
            size={{
              lg: 9,
              xs: 12,
            }}
          >
            <MUITextField
              disabled={this.props.readOnly}
              value={
                this.state.intakeQuestionnaire
                  ? this.state.intakeQuestionnaire
                      .patientCommitToProcessComments
                  : ""
              }
              multiline
              fullWidth={true}
              label={"Comments (optional)"}
              onChange={(e) =>
                this.handleChange({
                  patientCommitToProcessComments: e.target.value,
                })
              }
              variant="standard"
              slotProps={{
                htmlInput: { style: { resize: "both" } },
                inputLabel: { shrink: true },
              }}
            />
          </MUIGrid>
        </MUIGrid>
      </MUIBox>
    );
  }

  renderReasonForEquipment() {
    const handleBlur = (e) => {
      if (this.props.intakeQuestionnaire?.reasonForEquipment) {
        this.handleChange({ reasonForEquipment: e.target.value.trim() });
      }
    };
    return (
      <MUIBox>
        <MUITypography variant="body1" sx={{ marginBottom: 2 }}>
          What is the reason for the equipment?
        </MUITypography>
        <MUITextField
          disabled={this.props.readOnly}
          value={
            this.state.intakeQuestionnaire
              ? this.state.intakeQuestionnaire.reasonForEquipment
              : ""
          }
          multiline
          fullWidth={true}
          label={"Comments"}
          onChange={(e) =>
            this.handleChange({ reasonForEquipment: e.target.value })
          }
          onBlur={handleBlur}
          variant="standard"
          required={true}
          error={this.props.validationIssues?.reasonForEquipment ? true : false}
          helperText={
            this.props.validationIssues
              ? this.props.validationIssues.reasonForEquipment
              : ""
          }
          slotProps={{
            htmlInput: { style: { resize: "both" } },
            inputLabel: { shrink: true },
          }}
        />
      </MUIBox>
    );
  }

  renderCurrentMobilityAssistiveEquipment() {
    return (
      <MUIBox>
        <MUITypography variant="body1" sx={{ marginBottom: 2 }}>
          Do you currently have any type of mobility assistive equipment?
        </MUITypography>
        <MUIGrid
          container
          spacing={1}
          sx={{
            alignItems: "center",
          }}
        >
          <MUIGrid
            size={{
              lg: 3,
              xs: 12,
            }}
          >
            <MUIFormControl
              component="fieldset"
              required
              error={
                this.props.validationIssues.currentMobilityAssistiveEquipment
                  ? true
                  : false
              }
            >
              <MUIFormLabel sx={{ typography: "caption" }}>
                Select One
              </MUIFormLabel>
              <MUIRadioGroup
                row
                value={
                  this.state.intakeQuestionnaire
                    .currentMobilityAssistiveEquipment !== undefined
                    ? this.state.intakeQuestionnaire
                        .currentMobilityAssistiveEquipment
                    : null
                }
                onChange={(e) => {
                  const isTrue = e.target.value === "true";
                  this.handleChange({
                    currentMobilityAssistiveEquipment: isTrue,
                    ...(isTrue === false && {
                      currentMobilityAssistiveEquipmentDate: null,
                    }),
                  });
                }}
              >
                <MUIFormControlLabel
                  disabled={this.props.readOnly}
                  value={true}
                  control={<MUIRadio size="small" />}
                  label="Yes"
                />
                <MUIFormControlLabel
                  disabled={this.props.readOnly}
                  value={false}
                  control={<MUIRadio size="small" />}
                  label="No"
                />
              </MUIRadioGroup>
            </MUIFormControl>
          </MUIGrid>
          {this.state.intakeQuestionnaire.currentMobilityAssistiveEquipment ===
          true ? (
            <MUIGrid
              size={{
                lg: 3,
                xs: 12,
              }}
            >
              <MUIDatePicker
                disabled={this.props.readOnly}
                value={
                  this.state.intakeQuestionnaire &&
                  this.state.intakeQuestionnaire
                    .currentMobilityAssistiveEquipmentDate
                    ? moment(
                        this.state.intakeQuestionnaire
                          .currentMobilityAssistiveEquipmentDate,
                      )
                    : null
                }
                label="Date Received"
                slotProps={{
                  textField: {
                    size: "small",
                    variant: "standard",
                    required: true,
                    error: this.props.validationIssues
                      .currentMobilityAssistiveEquipmentDate
                      ? true
                      : false,
                    helperText: this.props.validationIssues
                      .currentMobilityAssistiveEquipmentDate
                      ? this.props.validationIssues
                          .currentMobilityAssistiveEquipmentDate
                      : "",
                    InputLabelProps: {
                      shrink: true,
                    },
                  },
                }}
                onChange={(value) =>
                  this.handleChange({
                    currentMobilityAssistiveEquipmentDate: value,
                  })
                }
                views={["year", "month"]}
                maxDate={moment(new Date())}
              />
            </MUIGrid>
          ) : null}
        </MUIGrid>
      </MUIBox>
    );
  }

  /**
   * Render body of Intake Questionaire dialog.
   */
  renderBody() {
    const handleChange = (updatedOrder) => {
      this.setState({
        order: {
          ...updatedOrder,
        },
      });
    };

    return (
      <MUIBox>
        {this.props.isLoading ? (
          <Progress message="Loading data..." />
        ) : (
          <>
            {this.state.step === 0 && (
              <InsuranceVerification
                order={this.state.order}
                onChange={handleChange.bind(this)}
                hasSecondaryInsurance={
                  !!(
                    this.state.order?.secondaryInsuranceTypeId ||
                    this.state.order?.secondaryInsuranceSubTypeId ||
                    this.state.order?.secondaryInsuranceGroup ||
                    this.state.order?.secondaryInsurancePolicy
                  )
                }
                onCheckEligibility={(order, fieldPrefix) =>
                  this.props.onCheckEligibility(order, fieldPrefix)
                }
                onCheckSameSim={(order) => this.props.onCheckSameSim(order)}
                insuranceLoading={this.props.insuranceLoading}
                secondaryInsuranceLoading={this.props.secondaryInsuranceLoading}
                sameSimVerificationLoading={
                  this.props.sameSimVerificationLoading
                }
                validationIssues={this.props.eligibilityValidationIssues}
                insuranceVerification={this.props.insuranceVerification}
                sameSimResults={this.props.sameSimResults}
                secondaryInsuranceVerification={
                  this.props.secondaryInsuranceVerification
                }
                onUpdateVerifiedInsurances={(verifiedInsurances) =>
                  this.setState({ verifiedInsurances: verifiedInsurances })
                }
              />
            )}
            {this.state.step === 1 && (
              <MUIList sx={{ listStyle: "decimal", pl: 3 }}>
                <MUIListItem sx={{ display: "list-item", pl: 0 }}>
                  <MUIListItemText primary={this.renderHomeHealth()} />
                </MUIListItem>
                <MUIListItem sx={{ display: "list-item", pl: 0 }}>
                  <MUIListItemText
                    primary={this.renderPlanToChangeInsurance()}
                  />
                </MUIListItem>
                <MUIListItem sx={{ display: "list-item", pl: 0 }}>
                  <MUIListItemText primary={this.renderPhysicianDate()} />
                </MUIListItem>
                <MUIListItem sx={{ display: "list-item", pl: 0 }}>
                  <MUIListItemText primary={this.renderPatientCommit()} />
                </MUIListItem>
                <MUIListItem sx={{ display: "list-item", pl: 0 }}>
                  <MUIListItemText primary={this.renderReasonForEquipment()} />
                </MUIListItem>
                <MUIListItem sx={{ display: "list-item", pl: 0 }}>
                  <MUIListItemText
                    primary={this.renderCurrentMobilityAssistiveEquipment()}
                  />
                </MUIListItem>
              </MUIList>
            )}
          </>
        )}
      </MUIBox>
    );
  }

  /**
   * Render cancel and confirm button actions of dialog.
   */
  renderActions() {
    const shouldDisableNextButton = () => {
      let shouldDisable = false;
      if (
        this.state.verifiedInsurances &&
        this.state.verifiedInsurances.length > 0 &&
        !this.state.order.verifiedInsurance
      ) {
        shouldDisable = true;
      }
      return shouldDisable;
    };
    return (
      <MUIDialogActions spacing={2}>
        <MUIButton variant="text" onClick={this.props.onClose} color="error">
          Cancel
        </MUIButton>

        {this.state.step === 0 ? (
          <MUIButton
            variant="contained"
            disabled={shouldDisableNextButton()}
            onClick={() => this.setState({ step: 1 })}
            endIcon={<MUIArrowForwardIcon />}
          >
            Next
          </MUIButton>
        ) : (
          <MUIStack direction="row" spacing={2}>
            <MUIButton
              variant="text"
              onClick={() => this.setState({ step: 0 })}
              startIcon={<MUIArrowBackIcon />}
            >
              Back
            </MUIButton>
            <MUIButton
              variant="contained"
              loading={this.props.loading}
              onClick={() =>
                this.props.onConfirm(
                  this.state.intakeQuestionnaire,
                  this.state.order,
                )
              }
            >
              Submit
            </MUIButton>
          </MUIStack>
        )}
      </MUIDialogActions>
    );
  }

  /**
   * @returns {string} The max width of the dialog.
   */
  getMaxWidth() {
    return "md";
  }
}
